// Question Card Deck Component
// Stack of question cards with a smooth, Tinder-style swipe interaction.

import React, { useState, useCallback } from 'react';
import { motion, useMotionValue, useTransform, animate, PanInfo } from 'framer-motion';
import { QuestionCard } from '../QuestionCard/QuestionCard';
import { QuestionCardStackContainer } from '../../../styles/HeartGames/QuestionCard.styles';
import { QuestionCard as QuestionCardType } from '../../../services/HeartGames/questionService';
import { GameAnswer } from '../../../types/heartGames';

interface QuestionCardDeckProps {
  questions: QuestionCardType[];
  onAnswer: (answer: GameAnswer) => void;
  onComplete: (answers: GameAnswer[]) => void;
  // Previously-answered questions (questionId -> optionId), used to resume an
  // in-progress game: the deck opens at the first still-unanswered question.
  initialSelectedAnswers?: Record<string, string>;
}

// Only render the top card plus a couple behind it — a solid card fully
// covers everything deeper, so drawing the whole deck is wasted work.
const VISIBLE_BEHIND = 2;
// How far a card must travel (or how fast it must be flung) to count as a swipe.
const DISTANCE_THRESHOLD = 90;
const VELOCITY_THRESHOLD = 400;
// Where a released card flies to when the swipe commits.
const FLY_OFF_X = 640;

export const QuestionCardDeck: React.FC<QuestionCardDeckProps> = ({
  questions,
  onAnswer,
  onComplete,
  initialSelectedAnswers,
}) => {
  // Seed from any restored answers, and open at the first unanswered question.
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>(
    () => initialSelectedAnswers ?? {}
  );
  const [currentIndex, setCurrentIndex] = useState(() => {
    const seed = initialSelectedAnswers ?? {};
    const firstUnanswered = questions.findIndex((q) => !seed[q.id]);
    return firstUnanswered === -1 ? Math.max(0, questions.length - 1) : firstUnanswered;
  });
  const [isAnimating, setIsAnimating] = useState(false);

  const currentQuestion = questions[currentIndex];
  const selectedAnswerId = currentQuestion ? selectedAnswers[currentQuestion.id] || null : null;

  // The top card's live horizontal position, with rotation + edge-fade derived from it.
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-16, 16]);
  const opacity = useTransform(x, [-FLY_OFF_X, -380, 0, 380, FLY_OFF_X], [0, 1, 1, 1, 0]);

  const handleAnswerSelect = useCallback((answerId: string) => {
    if (!currentQuestion || isAnimating) return;
    setSelectedAnswers((prev) => ({ ...prev, [currentQuestion.id]: answerId }));
  }, [currentQuestion, isAnimating]);

  // Advance to the next card once the fly-off animation has finished.
  const advanceToNext = useCallback(() => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      x.set(0); // reset instantly while the (now off-screen) card is unmounted from the top slot
      setIsAnimating(false);
    } else {
      const allAnswers: GameAnswer[] = questions.map((q) => ({
        questionId: q.id,
        optionId: selectedAnswers[q.id] || '',
        responseTime: 0,
        timestamp: new Date(),
      }));
      onComplete(allAnswers);
    }
  }, [currentIndex, questions, selectedAnswers, onComplete, x]);

  const springBack = useCallback(() => {
    animate(x, 0, { type: 'spring', stiffness: 500, damping: 40 });
  }, [x]);

  const handleDragEnd = useCallback((_event: unknown, info: PanInfo) => {
    if (isAnimating) return;

    // No answer chosen yet — never commit, just ease back to center.
    if (!currentQuestion || !selectedAnswerId) {
      springBack();
      return;
    }

    const committed =
      Math.abs(info.offset.x) > DISTANCE_THRESHOLD ||
      Math.abs(info.velocity.x) > VELOCITY_THRESHOLD;

    if (!committed) {
      springBack();
      return;
    }

    setIsAnimating(true);
    const direction = (info.offset.x || info.velocity.x) > 0 ? 1 : -1;

    const selectedOption = currentQuestion.options.find((opt) => opt.id === selectedAnswerId);
    if (selectedOption) {
      onAnswer({
        questionId: currentQuestion.id,
        optionId: selectedAnswerId,
        responseTime: 0,
        timestamp: new Date(),
      });
    }

    // Carry the drag's momentum into the fly-off so it feels physical.
    animate(x, direction * FLY_OFF_X, {
      type: 'spring',
      stiffness: 220,
      damping: 30,
      velocity: info.velocity.x,
      onComplete: advanceToNext,
    });
  }, [isAnimating, currentQuestion, selectedAnswerId, onAnswer, x, advanceToNext, springBack]);

  if (!currentQuestion) {
    return null;
  }

  return (
    <QuestionCardStackContainer>
      {questions.map((question, index) => {
        const distance = index - currentIndex;
        // Past cards are gone; only the top + a couple behind are drawn.
        if (distance < 0 || distance > VISIBLE_BEHIND) return null;

        const isTop = distance === 0;
        const zIndex = questions.length - index;

        return (
          <motion.div
            key={question.id}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex,
              // Drag transforms apply to the top card only.
              x: isTop ? x : 0,
              rotate: isTop ? rotate : 0,
              opacity: isTop ? opacity : 1,
              willChange: 'transform',
              cursor: isTop && selectedAnswerId && !isAnimating ? 'grab' : 'default',
            }}
            // Resting stack position (behind cards sit slightly lower + smaller);
            // spring-animates as a card is promoted to the top slot.
            animate={{ y: distance * 12, scale: 1 - distance * 0.045 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            drag={isTop && selectedAnswerId && !isAnimating ? 'x' : false}
            dragElastic={0.9}
            dragMomentum={false}
            onDragEnd={handleDragEnd}
            whileTap={isTop && selectedAnswerId && !isAnimating ? { cursor: 'grabbing' } : undefined}
          >
            <QuestionCard
              question={question}
              questionNumber={index + 1}
              totalQuestions={questions.length}
              selectedAnswerId={selectedAnswers[question.id] || null}
              onAnswerSelect={handleAnswerSelect}
              zIndex={zIndex}
              isTopCard={isTop}
              offset={0}
              rotation={0}
            />
          </motion.div>
        );
      })}
    </QuestionCardStackContainer>
  );
};
